import type { FilterNode } from '@ofmos/shared/filter-ast';
import { RECORD_QUERY_DEFAULT_LIMIT } from '@ofmos/shared/record-query';
import { materialiseViewFilter } from '@ofmos/shared/view';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import type { SavedView } from '../../data/client';
import {
  useCreateView,
  useDeleteView,
  useFields,
  useObject,
  useUpdateView,
  useViews,
} from '../../data/hooks';
import {
  Button,
  cx,
  EmptyState,
  SegmentedControl,
  Skeleton,
  useIsDesktop,
  useToast,
} from '../../ui';
import { ExportAction } from '../csv/ExportAction';
import { relationNeedsOfNode } from '../filters/filterModel';
import { useRelationContext } from '../filters/useRelationContext';
import { NotFound } from '../NotFound';
import { useRouteTransition } from '../nav/RouteTransition';
import { useLocalChoice } from '../record/useLocalToggle';
import { PageHeader } from '../shell/PageHeader';
import { PaneHost } from '../shell/PaneHost';
import { CreateRecordDialog } from './CreateRecordDialog';
import { KanbanView } from './kanban/KanbanView';
import { LIST_MODES, ListAdjustSheet } from './ListAdjustSheet';
import {
  detailDefaultWidth,
  detailMinWidth,
  ListDetailPane,
  useDetailMaxWidth,
  useRememberedDetailWidth,
} from './ListDetailPane';
import { NO_VIEW, useLastViewStorage } from './lastView';
import { ListHeaderRow } from './table/ListHeaderRow';
import { PhoneFilterRow } from './table/PhoneFilterRow';
import { PAGE_SIZE_CHOICES, PAGE_SIZE_STORAGE_KEY } from './table/pageSize';
import { RecordTableView } from './table/RecordTableView';
import { RowSelectionProvider } from './table/RowSelectionProvider';
import type { RecordSegment } from './table/TableToolbar';
import { tableColumns, visibleColumns } from './table/tableModel';
import { ViewOptions } from './table/ViewOptions';
import { FilterRefusedBanner } from './views/FilterRefusedBanner';
import { readSelection, resolveView, writeSelection } from './views/urlContract';
import { ViewIssueBanner } from './views/ViewIssueBanner';
import { ViewNameDialog } from './views/ViewNameDialog';
import { ViewSwitcher } from './views/ViewSwitcher';
import {
  isDirty,
  reconcileView,
  resolveBoardGroupField,
  toViewPatch,
  toViewWrite,
  UNSAVED_VIEW,
  type ViewIssue,
  type ViewState,
} from './views/viewState';

/**
 * The list screen. It owns the object, its fields and - since this module - THE WHOLE
 * VIEW STATE (D20): the filter, the sort, the visible columns, table-or-board and which
 * field a board groups by.
 *
 * That lift is the point rather than a tidy-up. Switching from table to board UNMOUNTS
 * one component and mounts the other, so everything the first one knew was discarded on
 * the way - a filter typed in the table did not survive the trip to the board, and a
 * saved view could not exist at all while the state it describes lived in a leaf.
 *
 * THE ARCHIVED SEGMENT MOVED UP TOO, on 2026-07-29, and it is still NOT part of a view
 * and still not in the URL (D15): sending somebody "the board" is a thing people do,
 * sending somebody "the archive of this object" is not. What changed is only which
 * component holds the useState, and it had to change - below the desktop breakpoint the
 * segment is a section of the Adjust sheet, which this page opens in board mode as well,
 * where `RecordTableView` does not exist to hold anything.
 *
 * TWO CHROME LAYOUTS, NOT ONE (Arif on his phone, 2026-07-29). The desktop keeps the
 * header actions and the toolbar row it had. The phone gets the title row with a single
 * "+" and ONE control row - the view switcher and Adjust - because it was stacking six
 * rows of buttons above the first record.
 */
export function RecordListPage() {
  const { objectId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  /**
   * Creating a record LANDS ON ITS PAGE, which is the same trip the card list makes and
   * therefore the same push. Without this the one navigation that is not a link click
   * would be the one that swapped screens without moving.
   *
   * The record is made in the dialog now (polish wave 3, audit P1-3) and the trip is the
   * same one: a record that exists because somebody named it is a record they want to be
   * looking at, and the landing is also what answers the audit's finding that a fresh row
   * is on page one of no list and in no board column.
   */
  const runTransition = useRouteTransition();
  /**
   * The two layouts are genuinely different COMPONENTS - an icon button instead of three
   * capsules, a sheet instead of a toolbar - which is exactly the case this hook exists
   * for (see ui/useIsDesktop). Doing it in CSS would leave both sets mounted, and the
   * hidden one would still be in the accessibility tree and in every test's query.
   */
  const isDesktop = useIsDesktop();

  const object = useObject(objectId);
  const fields = useFields(objectId);
  const views = useViews(objectId);
  const createView = useCreateView(objectId);
  const updateView = useUpdateView(objectId);
  const deleteView = useDeleteView(objectId);

  const selection = readSelection(searchParams);
  const current = resolveView(views.data ?? [], selection);

  /**
   * D27's ANCHOR SET, READ ONCE FOR THIS SCREEN (D21, D19).
   *
   * The page needs it for three different jobs and reading it three times would be three
   * answers: `reconcileView` validates a stored anchor against it, the toolbar's chip reads
   * a sentence out of it, and the refusal banner names an object with it. The fields behind
   * it are loaded only for the objects THIS filter names, which is what keeps a list page
   * that carries no related filter at exactly one extra request.
   */
  const [state, setState] = useState<ViewState>(UNSAVED_VIEW);
  const [search, setSearch] = useState({ objectId, term: '' });
  const searchTerm = search.objectId === objectId ? search.term : '';
  const setSearchTerm = (term: string) => setSearch({ objectId, term });
  const [segment, setSegment] = useState<RecordSegment>('active');
  const [baseline, setBaseline] = useState<ViewState>(UNSAVED_VIEW);
  const [issues, setIssues] = useState<readonly ViewIssue[]>([]);
  const [nameDialog, setNameDialog] = useState<'create' | 'rename' | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  /** P1-3: every create on this screen opens the same dialog, and nothing is written yet. */
  const [createOpen, setCreateOpen] = useState(false);
  /**
   * D17 M8's browser half. A view whose stored filter did not PARSE comes back with
   * `filterDamaged: true` and `filter: null`, and those two together are the trap: `null`
   * alone is indistinguishable from "no filter", so RUNNING the view would show every
   * record under a view name that promises fewer - the silent widening this module exists
   * to prevent, arriving through the module itself.
   *
   * So the page refuses to run it and renders the sentence instead. Clearing is the
   * reader's own click, and it is remembered per LOAD rather than per view: reopening the
   * view puts the damaged state back, because nothing was written.
   */
  const [damagedCleared, setDamagedCleared] = useState(false);

  /**
   * ROWS PER PAGE, LIFTED OUT OF THE TABLE (P4-5, `table-completion.md` D4). Same key, same
   * choices, same server default; what moved is only which component holds it, because the
   * control that changes it is now in the header row's view options and the sentence that
   * reports it is in the footer, and those are two different children of this page.
   */
  const [pageSize, setPageSize] = useLocalChoice(
    PAGE_SIZE_STORAGE_KEY,
    PAGE_SIZE_CHOICES,
    RECORD_QUERY_DEFAULT_LIMIT,
  );
  /**
   * The detail pane's width, dragged by its divider (P4-1). The two token values are read
   * once so the splitter can ANNOUNCE the width it really has: a pane that has never been
   * dragged is at `--list-detail-width`, and a control that reported nought there would be
   * lying to the one reader who cannot see it.
   */
  const [detailWidth, setDetailWidth] = useRememberedDetailWidth();
  const [detailDefaults] = useState(() => ({
    width: detailDefaultWidth(),
    min: detailMinWidth(),
  }));
  const detailPx = detailWidth ?? detailDefaults.width;
  /** The box the pane's ceiling is half of, read off the DOM (see `useDetailMaxWidth`). */
  const [split, setSplit] = useState<HTMLDivElement | null>(null);
  const detailMax = useDetailMaxWidth(split, detailDefaults.min);

  const relationNeeds = useMemo(() => relationNeedsOfNode(state.filter), [state.filter]);
  const relations = useRelationContext(objectId, relationNeeds);
  const anchorIds = useMemo(() => {
    const anchors = relations.anchorsOf(objectId);
    return anchors === undefined
      ? undefined
      : new Set(anchors.map((anchor) => anchor.anchorFieldId));
  }, [relations, objectId]);

  /**
   * LOADING A VIEW IS AN EDGE, not a render.
   *
   * Deriving the state on every render would throw away every filter the user typed the
   * moment anything refetched. So the reconciliation runs when the SELECTED VIEW CHANGES
   * - identified by its id and its `updatedAt`, so somebody else's save reaches this tab
   * too - and the result becomes both the live state and the baseline the dirty flag is
   * measured against.
   */
  const loadedRef = useRef<string | null>(null);
  /**
   * THE ANCHOR SET IS PART OF THE KEY, and leaving it out was a race with a visible wrong
   * answer. `pruneFilter` KEEPS a related node while the set is unknown (D19, wave 1's D-2),
   * which is right - dropping on "not known yet" would widen silently. But the effect below
   * stamps its key and never runs again, so a view reconciled BEFORE D27's read landed keeps
   * a node whose anchor may be archived, and the server then answers `invalid_state` and the
   * screen shows the generic error paragraph instead of "One filter was removed…".
   *
   * Which response wins that race is request timing, which is exactly how the D22 race in the
   * comment below went red on one branch and stayed green on another. So the key changes once
   * when the set arrives and the view is reconciled a second time against it. If the read
   * FAILS the key never changes and the view keeps its filter, which is wave 1's decided
   * default rather than a broken screen.
   */
  const loadKey =
    current === undefined
      ? `unsaved:${selection.viewId ?? ''}`
      : `${current.id}:${current.updatedAt}:${anchorIds === undefined ? 'pending' : 'known'}`;

  useEffect(() => {
    /**
     * NOTHING IS STAMPED UNTIL SEEDING IS POSSIBLE. This used to stamp `loadKey` first
     * and bail on missing fields after - so when the views response landed BEFORE the
     * metadata response on a fresh page load, the selected view's key was marked loaded
     * while the state was reset to UNSAVED, and the arrival of the fields hit the
     * early return: the switcher named the default view and the table quietly showed
     * everything, filter never applied. Which response wins that race is bundle timing,
     * which is how e2e D22 went red on one branch while main stayed green. Waiting says
     * nothing renders anyway until the fields exist (the page is a skeleton), so
     * deferring the whole edge is free.
     */
    if (fields.data === undefined) return;
    if (loadedRef.current === loadKey) return;
    loadedRef.current = loadKey;
    setDamagedCleared(false);
    if (current === undefined) {
      setState(UNSAVED_VIEW);
      setBaseline(UNSAVED_VIEW);
      setIssues([]);
      return;
    }
    // D18: damaged parts are dropped and reported; nothing is written until a save.
    const reconciled = reconcileView(current, fields.data, anchorIds);
    setState(reconciled.state);
    setBaseline(reconciled.state);
    setIssues(reconciled.issues);
  }, [loadKey, current, fields.data, anchorIds]);

  /**
   * WHICH VIEW THIS LIST REOPENS ON (`view-persistence.md` V1-V3, V5), in the two places
   * the decision allows: the effect below remembers the selection, the one after it
   * restores the memory on a mount whose URL names no view.
   *
   * The store is asked for rather than named, so WHERE the memory lives - since 2026-09-05
   * the per-user preference store - is answered in `lastView.ts` and nowhere else (V4).
   */
  const lastView = useLastViewStorage();

  /**
   * A LIST THAT DOES NOT KNOW WHICH VIEW IT IS YET SHOWS NOTHING (V5).
   *
   * `usePanels`' rule, for its reason: rendering the default view and swapping it a moment
   * later, when the person's remembered view arrives, reads as a bug rather than as a
   * preference. So a URL with no view in it waits for both answers it needs - the memory
   * and the view list - and the page's existing skeleton is what it waits behind.
   *
   * ONLY WHILE THEY ARE PENDING, never while they are broken: both flags go false on a
   * failed request, so a preference document or a view list that cannot be fetched opens
   * the screen on today's behaviour instead of holding a skeleton forever.
   */
  const decidingWhichView =
    selection.viewId === null && !selection.explicit && (!lastView.isReady || views.isPending);

  /**
   * REMEMBERING IT (V1, V2).
   *
   * AN EFFECT ON THE RESOLVED SELECTION, and not a line inside `selectView`, because a
   * deep link changes the selection without going through the switcher - and V2 says a URL
   * that names a view updates the memory. `selectView` is one of the ways in, not the way.
   *
   * A URL THAT NAMES NOTHING WRITES NOTHING. Landing on the default view is not choosing
   * it (V1 says "the view the user selected"), and remembering it would quietly outrank a
   * later change of default for somebody who has never picked a view at all.
   *
   * A URL WHOSE VIEW NO LONGER EXISTS WRITES NOTHING EITHER. `resolveView` has already
   * fallen back to the default for it, and overwriting a good memory with that fallback
   * would let one stale link somebody sent take away the list this person was working.
   *
   * IT WAITS FOR THE STORE TO BE READABLE, because the store skips a write that would
   * change nothing - and it can only know that once it holds the document. Writing first
   * would send a `PUT` for a value the server already has, on every list anybody opens.
   */
  const deletedViewRef = useRef<string | null>(null);
  useEffect(() => {
    if (!lastView.isReady) return;
    if (selection.explicit) {
      lastView.write(objectId, NO_VIEW);
      return;
    }
    if (selection.viewId === null || current?.id !== selection.viewId) return;
    // A view this page has just deleted, still named by a URL that has not caught up.
    if (deletedViewRef.current === selection.viewId) return;
    lastView.write(objectId, selection.viewId);
  }, [lastView, objectId, selection.explicit, selection.viewId, current]);

  /**
   * RESTORING IT (V2), once per object, on the mount that carries no view.
   *
   * IT REWRITES THE URL rather than resolving the view privately, because the URL is this
   * screen's contract (D19): a list showing "At risk" while the address bar says nothing is
   * a link that sends somebody else the default view. `replace` rather than a push, so Back
   * leaves the list instead of bouncing off this redirect.
   *
   * IT WAITS FOR THE VIEW LIST AND FOR THE MEMORY, because V3's question - is the
   * remembered view still there - cannot be asked before either arrives. A view that is
   * gone clears the key and says nothing: the bare URL then resolves to the default exactly
   * as it did before this package, which is the whole of "degrade silently". A view can
   * only be DELETED, never archived (`views/service.ts` soft-deletes and `listSavedViews`
   * filters those rows out), so "not in the list" is the only shape this case has.
   */
  const restoredRef = useRef<string | null>(null);
  useEffect(() => {
    /**
     * BOTH ANSWERS FIRST, AND THE STAMP AFTER THEM. The memory arrives over the network
     * now, so an effect that stamped itself as done before the document landed would read
     * "nothing remembered" from a store that had not answered yet - and then never look
     * again, because the stamp is what makes this run once.
     */
    if (!lastView.isReady || views.data === undefined) return;
    // The URL has already answered: it names a view, or it says "no view" out loud.
    if (selection.viewId !== null || selection.explicit) return;
    if (restoredRef.current === objectId) return;
    restoredRef.current = objectId;

    const remembered = lastView.read(objectId);
    if (remembered === null) return;
    if (remembered === NO_VIEW) {
      setSearchParams(
        writeSelection(searchParams, {
          viewId: null,
          /**
           * The unsaved list comes back in its OWN mode, not the one on screen: V1
           * remembers WHICH VIEW and nothing else, and the mode on screen at this moment
           * belongs to the default view this redirect is leaving.
           */
          modeOverride: UNSAVED_VIEW.mode,
          explicit: true,
        }),
        { replace: true },
      );
      return;
    }
    if (!views.data.some((view) => view.id === remembered)) {
      lastView.clear(objectId);
      return;
    }
    setSearchParams(
      writeSelection(searchParams, {
        viewId: remembered,
        // Whatever the URL said about the mode is left alone: the memory has nothing to say
        // about it, and `writeSelection` merges rather than replaces for exactly this.
        modeOverride: selection.modeOverride,
        explicit: false,
      }),
      { replace: true },
    );
  }, [
    lastView,
    objectId,
    views.data,
    selection.viewId,
    selection.explicit,
    selection.modeOverride,
    searchParams,
    setSearchParams,
  ]);

  /** The view owns the mode; `&mode=` (and the legacy literals) override it (D19). */
  const mode = selection.modeOverride ?? state.mode;

  /**
   * D17, resolved HERE and in the BROWSER (open risk 6). The server is UTC and Ben is in
   * Germany; the person reading "due this week" is the person whose week it is. Recomputed
   * when the stored filter changes rather than on a timer: a view that silently re-queried
   * at midnight would be a list that moved under somebody's cursor.
   */
  const materialised = useMemo<FilterNode | undefined>(() => {
    const viewFilter =
      state.filter === null ? undefined : materialiseViewFilter(state.filter, new Date());
    if (searchTerm.trim() === '' || object.data === undefined) return viewFilter;
    const searchFilter: FilterNode = {
      kind: 'condition',
      fieldId: object.data.labelIdentifierFieldId,
      operator: 'contains',
      value: searchTerm.trim(),
    };
    return viewFilter === undefined
      ? searchFilter
      : { kind: 'group', combinator: 'and', children: [viewFilter, searchFilter] };
  }, [state.filter, searchTerm, object.data]);

  const dirty = isDirty({ ...state, mode }, baseline);
  /** No table, no board, and therefore no record query at all while this is true. */
  const filterDamaged = current?.filterDamaged === true && !damagedCleared;

  /**
   * D32: THE EXPORT'S SCOPE IS WHAT THIS SCREEN IS SHOWING, so the columns it sends are
   * the ones on screen and not the `select` the list QUERIES with. Those are deliberately
   * different: the list selects every field so that hiding a column does not change its
   * cache key (INV-13), while the export's `select` is the visible set, because that is
   * what the person looking at it means by "these columns".
   */
  const shownColumns = useMemo(() => {
    if (fields.data === undefined || object.data === undefined) return [];
    const columns = tableColumns(object.data, fields.data);
    return state.columnFieldIds.length === 0
      ? columns
      : visibleColumns(
          columns,
          columns
            .filter((column) => !state.columnFieldIds.includes(column.field.id))
            .map((column) => column.field.id),
        );
  }, [fields.data, object.data, state.columnFieldIds]);
  const exportColumnFieldIds = useMemo(
    () => shownColumns.map((column) => column.field.id),
    [shownColumns],
  );
  /**
   * WHAT "STAGE" MEANS ON THIS VIEW (P4 round 2, finding F2), for the DETAIL PANE's action.
   * The same derivation `RecordTableView` makes for the row menu, from the same two helpers:
   * the pane's "Set ..." button and the menu's submenu must name one field, and round 1 had
   * both of them naming the object's first select rather than the one on screen.
   */
  const viewFields = useMemo(() => shownColumns.map((column) => column.field), [shownColumns]);

  if (object.isError) {
    return <NotFound />;
  }
  /**
   * A failed FIELD load is its own state, not a skeleton that never resolves: the screen is
   * generated from the field list, so without it there is nothing to render and the user has
   * to be able to retry.
   */
  if (fields.isError) {
    return (
      <EmptyState
        icon="warning"
        title="Unable to load this object."
        description={fields.error.message}
        action={
          <Button variant="secondary" onClick={() => void fields.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }
  if (object.data === undefined || fields.data === undefined || decidingWhichView) {
    return (
      <div className="space-y-4 py-6" aria-busy="true">
        <Skeleton className="h-8 w-48" />
        <Skeleton shape="block" className="h-64" />
      </div>
    );
  }

  const setMode = (next: 'table' | 'board') => {
    /**
     * MERGED over the existing params, never replacing them (D19). The old
     * `setSearchParams({ view: next })` replaced the WHOLE search string, so the mode
     * toggle would have wiped a `?view=<uuid>` the moment one existed.
     */
    setSearchParams(
      writeSelection(searchParams, {
        viewId: current?.id ?? null,
        // On a saved view the mode is an OVERRIDE and lives in the URL; on an unsaved
        // list it IS the legacy `?view=table|board`, which `writeSelection` writes.
        modeOverride: next,
        explicit: current === undefined,
      }),
      { replace: true },
    );
    setState((currentState) => ({ ...currentState, mode: next }));
  };

  const selectView = (viewId: string | null) => {
    /**
     * Choosing a view drops the mode override: the next view owns its own mode, and
     * carrying an override across would show somebody else's board as a table.
     *
     * Choosing "All records" is the opposite case and needs the URL to SAY so. A bare
     * URL means "the default view" (D22), so clearing the query string would bounce the
     * user straight back onto the default they just left. The legacy literal is the
     * shape that means "no view, this mode", so that is what it writes - and it keeps
     * the shape every existing link already uses.
     */
    setSearchParams(
      writeSelection(searchParams, {
        viewId,
        modeOverride: viewId === null ? mode : null,
        explicit: viewId === null,
      }),
      { replace: false },
    );
  };

  /**
   * WHERE A DELETED VIEW LEAVES YOU (Arif, 2026-09-05): the object's DEFAULT view, and the
   * memory forgotten.
   *
   * Deleting the thing you are looking at returns you to the list's home, and an unsaved
   * "All records" is not a home. So this writes the BARE URL - which is exactly what "the
   * default view" is on the wire (D22) - rather than `selectView(null)`, whose whole job is
   * to say "no view" out loud.
   *
   * IT FORGETS RATHER THAN REMEMBERING THE DEFAULT, on `view-persistence.md` V1's word
   * "selected": being sent home is not choosing home, and the memory that pointed at the
   * deleted view has to go either way (V3).
   *
   * `replace` and not a push: the entry being left names a view that no longer exists, so
   * a Back into it would resolve to the default anyway - one screen, two URLs.
   *
   * THE DEAD ID IS NAMED BEFORE ANYTHING ELSE HAPPENS, and that guard is the fix for a
   * measured bug rather than a precaution. Clearing the memory leaves a moment in which the
   * memory is empty while the URL still names the deleted view, and the remember effect
   * above - doing exactly its job - writes that view straight back: three requests in this
   * order, watched on the wire on 2026-09-05, `PUT <viewId>`, `PUT null`, `PUT <viewId>`.
   * Swapping the two lines does not close it, because the URL change lands in a LATER
   * render than the cache change however they are ordered here. So the page states the
   * fact instead: a view it has just deleted is never worth remembering, and the effect
   * reads that off `deletedViewRef`.
   */
  const openDefaultViewAfterDelete = (deletedViewId: string) => {
    deletedViewRef.current = deletedViewId;
    lastView.clear(objectId);
    setSearchParams(
      writeSelection(searchParams, { viewId: null, modeOverride: null, explicit: false }),
      { replace: true },
    );
  };

  const saveInto = (view: SavedView) => {
    updateView.mutate(
      { viewId: view.id, patch: toViewPatch({ ...state, mode }) },
      {
        onSuccess: () => {
          setBaseline({ ...state, mode });
          setIssues([]);
          toast({ title: `${view.name} was saved`, tone: 'success' });
        },
        onError: (error) =>
          toast({ title: 'Unable to save the view.', description: error.message, tone: 'danger' }),
      },
    );
  };

  const openCreated = (recordId: string) => {
    runTransition('push', () => {
      void navigate(`/objects/${objectId}/records/${recordId}`);
    });
  };

  /**
   * THE VIEW CHIP, DEFINED ONCE AND PLACED TWICE (P4-4). It is the first capsule of the
   * desktop's header row and the leading control of the phone's, and the ten callbacks
   * behind it belong to this page either way; writing it in both branches is how the two
   * placements start disagreeing about what Save As does.
   */
  const viewSwitcher = (
    <ViewSwitcher
      className="min-w-0 flex-1 md:flex-initial"
      views={views.data ?? []}
      current={current}
      dirty={dirty}
      isSaving={updateView.isPending}
      onSelect={selectView}
      onSave={() => {
        if (current !== undefined) saveInto(current);
      }}
      onSaveAs={() => {
        setNameError(null);
        setNameDialog('create');
      }}
      onRename={() => {
        setNameError(null);
        setNameDialog('rename');
      }}
      onSetDefault={() => {
        if (current === undefined) return;
        updateView.mutate(
          { viewId: current.id, patch: { isDefault: true } },
          {
            onSuccess: () =>
              toast({ title: `${current.name} opens by default now`, tone: 'success' }),
            onError: (error) =>
              toast({
                title: 'Unable to set the default.',
                description: error.message,
                tone: 'danger',
              }),
          },
        );
      }}
      onDelete={() => {
        if (current === undefined) return;
        deleteView.mutate(current.id, {
          onSuccess: () => {
            toast({ title: `${current.name} was deleted`, tone: 'neutral' });
            openDefaultViewAfterDelete(current.id);
          },
          onError: (error) =>
            toast({
              title: 'Unable to delete the view.',
              description: error.message,
              tone: 'danger',
            }),
        });
      }}
    />
  );

  /**
   * THE LIST'S OWN CONTROLS, DEFINED ONCE AND PLACED WHERE THE VIEW PUTS THEM (P4 round 2,
   * finding F3). In TABLE mode they belong to the list PANE and end at the divider, the way
   * Mail's message list carries its own header row while the message pane starts under the
   * toolbar; a board has no second pane, so there they are the page's first row as before.
   * Written once, because two copies of ten callbacks is how the two placements start
   * disagreeing about what Save As does - the same reason `viewSwitcher` above is one node.
   *
   * THE VIEW'S ISSUE BANNER TRAVELS WITH THEM: it is a sentence about the view the chip
   * names, so it belongs beside the chip rather than over the pane beside it.
   */
  const listControls = (
    <>
      {/*
        ONE HEADER ROW ON EACH VIEWPORT (P4-4).

        THE DESKTOP is Mail's construction: the view chip, one capsule per active filter,
        "Add Filter" and the view options, all in one line of capsules on the window
        background with nothing drawn around them (`ListHeaderRow`). The bordered toolbar
        strip that used to sit under this row is gone with it.

        THE PHONE keeps the row it has had since 2026-07-29 - which view on the left,
        everything else behind Adjust on the right - because a phone has no room for a
        line of capsules and the sheet is where those settings already live. Its top
        padding went with the object's description sentence (round 2, finding F4): this
        row is the FIRST content under the large title now, and the shell already insets
        content by the scroll edge's overhang.
      */}
      {isDesktop ? (
        <ListHeaderRow
          object={object.data}
          fields={fields.data}
          relations={relations}
          state={state}
          onStateChange={setState}
          segment={segment}
          onSegmentChange={setSegment}
          viewSwitcher={viewSwitcher}
        />
      ) : (
        <>
          <div className="flex items-center gap-2">
            {viewSwitcher}
            <ListAdjustSheet
              object={object.data}
              relations={relations}
              fields={fields.data}
              state={state}
              onStateChange={setState}
              mode={mode}
              onModeChange={setMode}
              segment={segment}
              onSegmentChange={setSegment}
              onDuplicates={() => void navigate(`/objects/${objectId}/duplicates`)}
              exportAction={
                <ExportAction
                  block
                  object={object.data}
                  columnFieldIds={exportColumnFieldIds}
                  filter={materialised}
                  orderBy={state.orderBy}
                  archived={segment === 'archived'}
                />
              }
            />
          </div>
          {/* THE TOKENS, UNDER THE BAR (wave 5 round 4, B4; spec/screens/list.md section 3 item
            2). The phone had none: what narrowed the list lived one drill-in deep inside the
            Adjust sheet, so a filtered view looked exactly like an unfiltered one. */}
          <PhoneFilterRow
            object={object.data}
            fields={fields.data}
            relations={relations}
            state={state}
            onStateChange={setState}
            segment={segment}
            onSegmentChange={setSegment}
          />
        </>
      )}

      {issues.length > 0 && (
        <div className="pt-3">
          <ViewIssueBanner
            issues={issues}
            isSaving={updateView.isPending}
            onSave={() => {
              if (current !== undefined) saveInto(current);
            }}
          />
        </div>
      )}
    </>
  );

  return (
    /**
     * THE SELECTION IS THE PAGE'S (P4-2), because the table and the detail pane are
     * siblings and both read it: the table paints it, the pane is about it.
     */
    <RowSelectionProvider>
      <div
        className={cx(
          /* `list-page` is the hook for the one rule the phone's create item needs
             (index.css, p4-list wave-3 block): the shell puts a page's actions on their own
             line under the large title, and a bar button item belongs at the trailing end
             of it. Nothing else on the page is styled by it. */
          'list-page flex min-h-0 flex-col',
          /**
           * THE PAGE IS EXACTLY AS TALL AS THE VIEWPORT IN TABLE MODE, from the desktop
           * breakpoint up (polish wave 2). That is the whole mechanism behind "the page
           * stands still and the table pans inside its card": a card can only be the
           * scrollport if nothing above it can scroll, and nothing above it can scroll only
           * if this page fits. `--page-viewport` is 100dvh minus the shell's own bottom
           * padding, so the sum is the viewport and the document never grows.
           *
           * THE BOARD IS THE SAME PAGE NOW (wave 5 round 3, C3-3). It used to keep the page
           * scroll it always had, because its columns capped themselves at 70dvh and
           * scrolled inside that cap. That cap is gone above the desktop breakpoint - the
           * board adopted the pane host, and the host is the screen's one vertical
           * scrollport - so the page has to fit for exactly the reason the table's does.
           */
          'md:h-(--page-viewport)',
        )}
      >
        <PageHeader
          title={object.data.labelPlural}
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            placeholder: `Search ${object.data.labelPlural.toLowerCase()}`,
          }}
          /**
           * NO DESCRIPTION SENTENCE ON THIS SCREEN (P4 round 2, finding F4). The object's
           * sentence sat between the toolbar and the capsule row at 1440 and under the large
           * title at 375, and it is a definition of the object rather than anything about the
           * rows: Mail's list carries "1 Message" - a count - and nothing else, spec section
           * 2 lists no description line, and the footer already states the count. The sentence
           * still has two homes where it teaches: the Objects and fields admin, which is where
           * it is written, and the empty state, which is the screen a first-time reader meets
           * (`RecordTableView`'s unfiltered arm reads `object.description`).
           */
          /**
           * THE PHONE'S CREATE ACTION IS A BAR ITEM AGAIN (wave 3, PH-2). It was a floating
           * round button over the rows, and at 375 it sat on top of them: the mockup review
           * caught it clearing the tab bar and the wave-3 triage caught it covering the text
           * of "Birch Studio" and "Cedar Studio" (list-375-light.png).
           *
           * WHERE APPLE PUTS AN ACTION: "A toolbar with navigation controls appears at the
           * top of a window, helping people move through a hierarchy of content. In iOS, a
           * navigation-specific toolbar is sometimes called a navigation bar", and of its
           * trailing edge, "It also includes a primary action like Done when one exists.
           * Items on the trailing edge remain visible at all window sizes"
           * (hig: https://developer.apple.com/design/human-interface-guidelines/toolbars).
           * The tab bar is not the place for it either way: "Use a tab bar to support
           * navigation, not to provide actions ... If you need to provide controls that act
           * on elements in the current view, use a toolbar instead"
           * (hig: https://developer.apple.com/design/human-interface-guidelines/tab-bars).
           *
           * AND NOT A BOTTOM TOOLBAR, which is the other placement the triage offered: the
           * shell's phone bottom toolbar (`PhoneToolbar`, S1) is fixed at the same bottom
           * inset as the tab bar and the list is a tab ROOT, so the two would occupy one
           * strip - the defect this finding is about, in a second shape. Notes can use a
           * bottom toolbar because Notes has no tab bar.
           *
           * The name stays the full "New Customer": a bar item's label is what a screen
           * reader reads out, and "New" alone says nothing about what it makes. It carries the
           * object's own singular label as configured (wave 4 round 2, finding 5): the copy
           * deck's List row for this control reads "New ${Singular}" as a navigation bar item,
           * and the pane one screen over generates "No Agency selected" from the same label
           * without lowering it (sentence case for the words around it, decision page item
           * 2.1). Round 1 lowered it, so the phone's bar read "New agency" beside a title that
           * did not.
           */
          {...(isDesktop
            ? {
                actions: (
                  <>
                    {/* A real segmented control: two exclusive views sitting side by side,
                      with the selection SLIDING between them - a spatial choice deserves
                      a spatial control. */}
                    <SegmentedControl
                      size="sm"
                      value={mode}
                      onValueChange={(next) => setMode(next === 'board' ? 'board' : 'table')}
                      ariaLabel="View"
                      segments={LIST_MODES.map((entry) => ({
                        value: entry.value,
                        label: entry.label,
                        icon: entry.icon,
                      }))}
                    />
                    {/*
                      THE VIEW OPTIONS ARE A TOOLBAR ITEM (wave 4, C12; spec/screens/list.md
                      section 2 item 3 puts "the view options button" in the bar and item 10
                      anchors its popover to it). Sort, Columns and Group were three capsules
                      in the list pane's header row, where they cost 243px of a 611px row and
                      left a filter token with no room for its operand; one symbol item in the
                      window frame is what the spec had all along. In table mode only: a board
                      has no columns to choose and no rows to sort.
                    */}
                    {mode === 'table' && (
                      <ViewOptions
                        object={object.data}
                        fields={fields.data}
                        relations={relations}
                        state={state}
                        onStateChange={setState}
                        segment={segment}
                        onSegmentChange={setSegment}
                        pageSize={pageSize}
                        onPageSizeChange={setPageSize}
                      />
                    )}
                    {/*
                    The object-level possible-duplicates list (identity-resolution.md 2.2).
                    A secondary action next to the view switch rather than a nav entry: it
                    is about THIS object's records, and it is work somebody does
                    deliberately, not a destination they navigate to from anywhere.
                  */}
                    <Button
                      variant="secondary"
                      icon="users"
                      onClick={() => void navigate(`/objects/${objectId}/duplicates`)}
                    >
                      Duplicates
                    </Button>
                    {/*
                    D32: it exports the QUERY, which is why it lives beside the controls
                    that shape it rather than under Settings. Absent without `EXPORT_CSV`.
                  */}
                    <ExportAction
                      object={object.data}
                      columnFieldIds={exportColumnFieldIds}
                      filter={materialised}
                      orderBy={state.orderBy}
                      archived={segment === 'archived'}
                    />
                    {/* The NAME is the full "New Agency" at both widths (C2-2). The bar
                        draws this item as its symbol master, so the word is what a screen
                        reader reads out and what the Mac's help tag says - and "New" alone
                        says nothing about what it makes, which is the rule the phone's own
                        item below has followed since wave 4 round 2. */}
                    <Button
                      variant="primary"
                      icon="plus"
                      onClick={() => setCreateOpen(true)}
                    >{`New ${object.data.labelSingular}`}</Button>
                  </>
                ),
              }
            : /**
               * NOT OVER THE ARCHIVE (audit P2-6, kept). Everything on that segment is a
               * tombstone, so an invitation to make a live record while somebody is looking
               * at deleted ones is an invitation to lose it: per P1-3 the new record is
               * invisible in the segment they are standing in. The desktop's New is absent
               * there for the same reason, so this is one rule and not two.
               */
              segment !== 'archived'
              ? {
                  actions: (
                    <Button variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>
                      {`New ${object.data.labelSingular}`}
                    </Button>
                  ),
                }
              : {})}
        />

        {fields.data.length === 0 ? (
          <EmptyState
            icon="settings"
            title="No fields"
            description="Add fields under Objects and fields; the table and the board are generated from them."
            action={
              <Button
                variant="secondary"
                onClick={() => void navigate(`/settings/objects/${objectId}`)}
              >
                Open Objects and fields
              </Button>
            }
          />
        ) : (
          <>
            {filterDamaged ? (
              <>
                {listControls}
                <div className="pt-3">
                  <FilterRefusedBanner
                    message="This view's filter cannot be read and has not been applied. Clear it to build a new one."
                    onClear={() => setDamagedCleared(true)}
                  />
                </div>
              </>
            ) : mode === 'table' ? (
              /**
               * THE SPLIT VIEW (P4-1): the list on the leading side, the selected record's
               * detail on the trailing side, one thin line between them and no frame around
               * either (spec/screens/list.md section 2 item 5). Below 1024px the pane is not
               * drawn at all and a row opens the record page, which `.list-detail` decides in
               * CSS so the pane leaves the accessibility tree with the paint.
               *
               * AND SINCE WAVE 5 ROUND 3 THE BOARD DRAWS THE SAME SPLIT (C3-3). The
               * argument that used to stand here - a board's columns ARE the arrangement, so
               * a pane beside them would compete with the card under the cursor - did not
               * survive the owner's click: a column says which STAGE a record is in and
               * nothing about the record itself, and a click on a card selects rather than
               * navigates, so there is no card "already open" to compete with.
               *
               * THE HEADER ROW IS INSIDE THE LIST PANE (P4 round 2, finding F3). It used to
               * span the whole content width, which put Sort, Columns and Group above the
               * DETAIL pane - controls that narrow and order the list, floating over the thing
               * beside it. MEASURED support-read-and-respond-to-emails-mlhlp1010__S0001_
               * MailWindow.png: the message list's own header row ends at the divider, and the
               * message pane starts directly under the toolbar with its own first line. Both
               * panes now begin at the same top edge and each carries its own first row.
               */
              <div ref={setSplit} className="list-split">
                <PaneHost host="object-list" className="list-pane">
                  {listControls}
                  <RecordTableView
                    object={object.data}
                    relations={relations}
                    fields={fields.data}
                    state={state}
                    filter={materialised}
                    onStateChange={setState}
                    segment={segment}
                    onCreate={() => setCreateOpen(true)}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </PaneHost>
                <ListDetailPane
                  object={object.data}
                  fields={viewFields}
                  archived={segment === 'archived'}
                  boardGroupFieldId={state.boardGroupFieldId}
                  width={detailWidth === null ? 'var(--list-detail-width)' : `${detailWidth}px`}
                  widthPx={detailPx}
                  minPx={detailDefaults.min}
                  maxPx={detailMax}
                  onWidth={setDetailWidth}
                />
              </div>
            ) : (
              /**
               * AND THE BOARD IS THE SAME SPLIT VIEW (wave 5 round 3, C3-3; owner: "Board
               * view nicht optimiert, kein Liquid Glass, keine Vorschau").
               *
               * THE CAUSE. The board was the one screen wave 5 never touched: it stood
               * OUTSIDE `PaneHost`, so nothing on it ever passed under the window toolbar
               * and the bar's blur, lens and tint had a flat strip to work on - a
               * displacement map on a constant field returns the constant, which is the
               * whole of "kein Liquid Glass". And the comment that used to stand here
               * argued a board needs no detail pane because "a board's columns ARE the
               * arrangement"; the owner read the same screen and asked for the preview. A
               * column says which STAGE a record is in; it says nothing about the record,
               * which is what the pane is for.
               *
               * NOTHING NEW IS DESIGNED: the host, the pane and the divider are the table's
               * own, with the board's own name on the host so the review can enumerate it.
               */
              <div ref={setSplit} className="list-split">
                <PaneHost host="object-board" className="list-pane">
                  {listControls}
                  <KanbanView
                    object={object.data}
                    fields={fields.data}
                    filter={materialised}
                    groupFieldId={resolveBoardGroupField(state.boardGroupFieldId, fields.data)}
                    onGroupFieldChange={(boardGroupFieldId) =>
                      setState((currentState) => ({ ...currentState, boardGroupFieldId }))
                    }
                  />
                </PaneHost>
                <ListDetailPane
                  object={object.data}
                  fields={viewFields}
                  archived={segment === 'archived'}
                  boardGroupFieldId={state.boardGroupFieldId}
                  width={detailWidth === null ? 'var(--list-detail-width)' : `${detailWidth}px`}
                  widthPx={detailPx}
                  minPx={detailDefaults.min}
                  maxPx={detailMax}
                  onWidth={setDetailWidth}
                />
              </div>
            )}
          </>
        )}

        {/**
         * ONE DIALOG FOR ALL THREE ENTRY POINTS on this screen - the header's New, the empty
         * state's CTA and the phone's floating button (audit P1-3's rows 1, 2 and 3). They
         * were three call sites of one mint function; they are three openers of one dialog,
         * so none of them can drift into its own answer.
         */}
        <CreateRecordDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          object={object.data}
          onCreated={openCreated}
        />

        <ViewNameDialog
          open={nameDialog !== null}
          onOpenChange={(next) => setNameDialog(next ? nameDialog : null)}
          mode={nameDialog ?? 'create'}
          initialName={nameDialog === 'rename' ? (current?.name ?? '') : ''}
          isSaving={createView.isPending || updateView.isPending}
          error={nameError}
          onSubmit={(name) => {
            setNameError(null);
            if (nameDialog === 'rename' && current !== undefined) {
              updateView.mutate(
                { viewId: current.id, patch: { name } },
                {
                  onSuccess: () => setNameDialog(null),
                  onError: (error) => setNameError(error.message),
                },
              );
              return;
            }
            createView.mutate(toViewWrite(objectId, name, { ...state, mode }, false), {
              onSuccess: (view) => {
                setNameDialog(null);
                setBaseline({ ...state, mode });
                setIssues([]);
                selectView(view.id);
              },
              onError: (error) => setNameError(error.message),
            });
          }}
        />
      </div>
    </RowSelectionProvider>
  );
}
